// The one field-rendering component of the unified renderer. Every input type
// in the catalog renders through here, in the builder's test mode and on the
// public form alike.
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { FieldDefinition, FieldOption, FieldUiMeta, FormConfig, FormRequiredStyle, FormUploadSettings } from '../shared/types';
import { resolveDateBound } from '../shared/types';
import { statesForCountry } from '../shared/states';
import { AddressValue, COUNTRIES, addressFormat, normalizeAddressValue, normalizeNameValue } from '../shared/address';
import { fieldOptions } from './config';
import SignatureInput from './SignatureInput';
import type { SignatureMode } from './model';
import { FieldError, errorAttrs } from './fieldError';
import FileInput from './FileInput';
import { resolveUploadLimits } from './uploadLimits';
import { STRINGS as S } from './strings';
import { RichBlocks, RichInline } from './richTextView';
import { formatUsPhone, usPhoneDigits, wantsUsPhone } from './phone';
import ScaleInput from './ScaleInput';
import MatrixInput from './MatrixInput';
import RankingInput from './RankingInput';
import { datetimeLocalPart, withLocalOffset } from './questionTypes';
import { ADDRESS_AUTOCOMPLETE, NAME_AUTOCOMPLETE, autoCompleteFor, enterKeyHintFor } from './autofill';

/** Text-like controls that get a character counter when a length rule is set (U3). */
const COUNTED_TYPES = ['text', 'textarea', 'email', 'tel', 'url'];
/** Types that can offer an "Other" write-in (U3). */
const OTHER_TYPES = ['radio', 'select', 'multiselect'];
/** The <option> a select uses for its "Other" choice; never stored — the typed text is the answer. */
const OTHER_VALUE = '__other__';
const DEFAULT_REQUIRED_STYLE: FormRequiredStyle = { marker: 'asterisk', optionalSuffix: true };

/** A date bound as a datetime-local limit: the day, at its first or last minute. */
const dayBound = (spec: string | undefined, time: string): string | undefined => {
  const day = resolveDateBound(spec);
  return day ? `${day}${time}` : undefined;
};

const COUNTRY_OPTS: FieldOption[] = [
  { value: 'United States', text: 'United States' },
  { value: 'Canada', text: 'Canada' },
];

export interface FieldInputProps {
  def: FieldDefinition;
  cfg: FormConfig;
  value: unknown;
  required: boolean;
  onChange: (v: unknown) => void;
  disabled?: boolean;
  /** ⚡ note under the input (mirrored / prefilled / computed) */
  note?: string;
  /** unique DOM id (instance-scoped ids come from the renderer) */
  domId: string;
  /** current values of this field's context — resolves optionsPreset country lookups */
  ctxValues?: Record<string, unknown>;
  /** the form's name — a token the terms above a signature pad may use */
  formName?: string;
  /** signature fields show what was signed and offer no pad (the edit drawer) */
  signaturesLocked?: boolean;
  /** composite sub-fields mirrored by a LOCKED prefill rule (render disabled) */
  lockedSubs?: string[];
  /** 'file' fields: currently picked files + change handler */
  files?: File[];
  onFiles?: (files: File[]) => void;
  /** 'file' fields: the form's upload settings, under the field's own `ui_meta.upload` */
  uploads?: FormUploadSettings;
  /** the control refused something itself (a file over the limit) — report it under the control */
  onSetError?: (message: string) => void;
  /** 'signature' fields: how this one was signed (draw or type) */
  onSignatureMode?: (mode: SignatureMode) => void;
  /** how this one WAS signed, so a pad remounted by the pager comes back in that mode */
  signatureMode?: SignatureMode;
  /** what is wrong with this answer, shown under the control (see payload.ts) */
  error?: string;
  /** a composite's per-part errors, keyed by sub-field ('firstName', 'postalCode') */
  subErrors?: Record<string, string>;
  /**
   * The errors-map key this control answers to (`inst.key` inside a widget),
   * stamped on the control so a summary item can find and focus it.
   */
  errorPath?: string;
  /** the answer changed — the renderer drops the error for it (or for one part) */
  onClearError?: (sub?: string) => void;
  /** how required / optional is marked on the label (`settings.requiredStyle`) */
  requiredStyle?: FormRequiredStyle;
}

/**
 * A heading block. Level 1–3 is what the builder picks; on the page it lands
 * one step under the section title (an <h2>), so the document outline stays
 * honest: form title, section, then the headings inside it.
 */
function Heading({ level, text }: { level: number | undefined; text: string }) {
  const n = Math.max(1, Math.min(3, Number(level) || 1));
  const Tag = (`h${n + 1}`) as 'h2' | 'h3' | 'h4';
  return <Tag className={`of-heading of-heading-${n}`}>{text}</Tag>;
}

/** An image block: nothing at all until it has a URL. */
function ImageBlock({ image }: { image: FieldUiMeta['image'] }) {
  const url = (image?.url || '').trim();
  if (!url) return null;
  const width = Number(image?.width);
  const align = image?.align === 'left' || image?.align === 'right' ? image.align : 'center';
  return (
    <figure className={`of-image of-image-${align}`}>
      <img src={url} alt={image?.alt || ''} style={width > 0 ? { width: `${width}px` } : undefined} />
    </figure>
  );
}

/** How a 'divider' draws: its own weight, colour and the air around it. */
function dividerStyle(d: FieldUiMeta['divider']): React.CSSProperties {
  const thickness = Math.max(1, Math.min(8, Number(d?.thickness) || 1));
  const space = Math.max(0, Math.min(80, Number(d?.space ?? 20)));
  return {
    borderTopWidth: thickness,
    ...(d?.color ? { borderTopColor: d.color } : {}),
    marginTop: space,
    marginBottom: space,
  };
}

/** Resolve a select's options, honoring ui_meta.optionsPreset. */
function resolveOptions(def: FieldDefinition, cfg: FormConfig, ctxValues: Record<string, unknown>): FieldOption[] {
  const m = (def.ui_meta || {}) as Record<string, unknown>;
  const preset = String(m.optionsPreset || '');
  if (preset === 'countries') return COUNTRY_OPTS;
  if (preset === 'us_states') {
    const countryKey = String(m.countryField || '');
    const country = countryKey ? String(ctxValues[countryKey] || '') : '';
    return statesForCountry(country || undefined).map((s) => ({ value: s, text: s }));
  }
  const opts = fieldOptions(def, cfg);
  // Legacy fallback: a select keyed like "…State" with no options gets US states.
  if (opts.length === 0 && /State$/.test(def.key)) {
    const country = String(ctxValues[def.key.replace(/State$/, 'Country')] || '');
    return statesForCountry(country || undefined).map((s) => ({ value: s, text: s }));
  }
  return opts;
}

export default function FieldInput({
  def, cfg, value, required, onChange, disabled, note, domId, ctxValues = {}, formName, signaturesLocked,
  lockedSubs = [], files, onFiles, uploads, onSetError, onSignatureMode, signatureMode, error, subErrors = {}, errorPath, onClearError,
  requiredStyle,
}: FieldInputProps) {
  const m = def.ui_meta || {};
  const rules = m.validation || {};
  const path = errorPath || def.key;
  const hasInfo = !!m.infoText;

  // "Other" (U3): the write-in is open while its choice is ticked. A stored
  // answer that is not one of the options IS an "other" answer, so the row
  // opens on its own for a resumed draft; this flag only covers the moment
  // between ticking Other and typing the first character.
  const [otherOn, setOtherOn] = useState(false);
  const otherRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (otherOn) otherRef.current?.focus(); }, [otherOn]);
  const other = OTHER_TYPES.includes(def.input_type) && m.allowOther
    ? { label: m.allowOther.label?.trim() || S.other } : null;
  const choiceOpts = OTHER_TYPES.includes(def.input_type) ? resolveOptions(def, cfg, ctxValues) : [];
  const known = new Set(choiceOpts.map((o) => o.value));
  const isOther = (v: string) => v !== '' && !known.has(v);
  const otherInput = (text: string, onText: (t: string) => void) => (
    <input
      ref={otherRef} id={`${domId}-other-text`} className="of-other-input" type="text"
      value={text} placeholder={S.otherPrompt} aria-label={`${def.label}: ${other?.label}`}
      disabled={disabled} onChange={(e) => onText(e.target.value)}
    />
  );
  // Announced as required (screen readers say so) without the browser's own
  // enforcement — the public form runs with `noValidate`, and the engine's
  // validator is what reports an empty answer, under the control.
  const ariaReq = required && !disabled ? (true as const) : undefined;
  const anyError = !!error || Object.keys(subErrors).length > 0;
  const groupClass = `of-group${anyError ? ' has-error' : ''}`;
  const change = (v: unknown) => { onClearError?.(); onChange(v); };
  const info = m.infoText ? <p className="of-info" id={`${domId}-info`}><RichInline text={m.infoText} /></p> : null;
  const noteEl = note ? <p className="of-info of-note">{note}</p> : null;
  const err = <FieldError id={`${domId}-err`} text={error} />;
  // Required wording (U3): an asterisk or the word "Required" after the
  // label, and "(optional)" after the rest unless the form turned it off.
  const rs = requiredStyle || DEFAULT_REQUIRED_STYLE;
  const markerFor = (req: boolean): ReactNode => (req && !disabled
    ? (rs.marker === 'label'
      ? <span className="of-required-label">{S.requiredLabel}</span>
      : <span className="of-required">*</span>)
    : !disabled && rs.optionalSuffix ? <span className="of-optional">{S.optional}</span> : null);
  const marker = markerFor(required);

  // Character counter (U3): only where a length rule exists. The limit is
  // still enforced by the validator, never by clipping — the count goes
  // amber near the ceiling and red past it, and the over-long answer stays
  // in the box so the respondent can see what to cut.
  const usPhone = wantsUsPhone(def);
  const counted = COUNTED_TYPES.includes(def.input_type) && (rules.maxLen !== undefined || rules.minLen !== undefined);
  const len = counted ? (usPhone ? usPhoneDigits(value) : String(value ?? '')).length : 0;
  const countTone = rules.maxLen === undefined ? '' : len > rules.maxLen ? ' over' : len >= rules.maxLen * 0.9 ? ' warn' : '';
  const counter = counted
    ? <span className={`of-count${countTone}`} id={`${domId}-count`} aria-live="polite">
        {rules.maxLen !== undefined ? S.count(len, rules.maxLen) : S.countMin(len, rules.minLen!)}
      </span>
    : null;

  // Presentation, not a question: a rule across the form with nothing to fill
  // in. It still answers to show/hide rules like anything else on the canvas.
  if (def.input_type === 'divider') return <hr className="of-divider" style={dividerStyle(m.divider)} />;
  // The other content blocks (U5): words and pictures between the questions.
  if (def.input_type === 'heading') return <Heading level={m.heading?.level} text={def.label} />;
  if (def.input_type === 'paragraph') return <RichBlocks className="of-paragraph" text={m.paragraph?.text || ''} />;
  if (def.input_type === 'image') return <ImageBlock image={m.image} />;

  if (def.input_type === 'radio' || def.input_type === 'multiselect') {
    const opts = choiceOpts;
    const multi = def.input_type === 'multiselect';
    // Image choice (P3): when any option carries a picture the list becomes a grid of tiles.
    const tiles = opts.some((o) => !!o.image);
    const picked = multi ? (Array.isArray(value) ? (value as string[]) : []) : [String(value ?? '')];
    const toggle = (v: string) => {
      setOtherOn(false);
      if (!multi) { change(v); return; }
      change(picked.includes(v) ? picked.filter((x) => x !== v) : [...picked, v]);
    };
    // The "other" answer: for a radio it is the value itself when it is not
    // an option; for a multiselect it is the list's one free-text entry.
    const otherText = picked.find(isOther) || '';
    const otherChecked = !!other && (otherText !== '' || (otherOn && (multi || picked[0] === '')));
    const toggleOther = () => {
      if (otherChecked) {
        setOtherOn(false);
        change(multi ? picked.filter((x) => !isOther(x)) : '');
      } else {
        setOtherOn(true);
        if (!multi) change('');
      }
    };
    const setOtherText = (t: string) => change(multi ? [...picked.filter((x) => !isOther(x)), ...(t ? [t] : [])] : t);
    return (
      <div
        className={groupClass} role={multi ? 'group' : 'radiogroup'} aria-labelledby={`${domId}-label`}
        aria-required={ariaReq} {...errorAttrs(domId, error, path, hasInfo)}
      >
        <label id={`${domId}-label`}>{def.label}{marker}</label>
        <div className={`of-choices${tiles ? ' of-tiles' : ''}`}>
          {opts.map((o, i) => (
            <div key={o.value} className={`of-checkbox of-choice${tiles ? ` of-tile${picked.includes(o.value) ? ' on' : ''}` : ''}`}>
              <input
                id={`${domId}-${i}`} name={domId}
                type={multi ? 'checkbox' : 'radio'}
                checked={picked.includes(o.value)}
                disabled={disabled}
                onChange={() => toggle(o.value)}
              />
              <label htmlFor={`${domId}-${i}`} style={{ marginBottom: 0, fontWeight: 400 }}>
                {tiles && (o.image
                  ? <img className="of-tile-img" src={o.image} alt="" />
                  : <span className="of-tile-img of-tile-blank" aria-hidden="true" />)}
                <span className="of-tile-text">{o.text}</span>
              </label>
            </div>
          ))}
          {other && (
            <div className="of-checkbox of-choice of-other">
              <input
                id={`${domId}-other`} name={domId}
                type={multi ? 'checkbox' : 'radio'}
                checked={otherChecked}
                disabled={disabled}
                onChange={toggleOther}
              />
              <label htmlFor={`${domId}-other`} style={{ marginBottom: 0, fontWeight: 400 }}>{other.label}</label>
            </div>
          )}
          {other && otherChecked && otherInput(otherText, setOtherText)}
        </div>
        {err}
        {noteEl}
        {info}
      </div>
    );
  }

  if (def.input_type === 'rating') {
    const top = Math.max(1, Math.min(10, Number(m.ratingMax) || 5));
    const current = Number(value) || 0;
    return (
      <div
        className={groupClass} role="radiogroup" aria-labelledby={`${domId}-label`}
        aria-required={ariaReq} {...errorAttrs(domId, error, path, hasInfo)}
      >
        <label id={`${domId}-label`}>{def.label}{marker}</label>
        <div className="of-rating">
          {Array.from({ length: top }, (_, i) => i + 1).map((n) => (
            <button
              key={n} type="button" disabled={disabled}
              className={`of-star ${n <= current ? 'on' : ''}`}
              aria-label={`${n} of ${top}`} aria-pressed={n === current}
              onClick={() => change(n === current ? '' : n)}
            >★</button>
          ))}
          {current > 0 && <span className="of-rating-count">{current} / {top}</span>}
        </div>
        {err}
        {noteEl}
        {info}
      </div>
    );
  }

  // P3: a scale is a radiogroup of numbered buttons; a matrix and a ranking
  // are groups whose first control takes the focus a summary item hands over.
  if (def.input_type === 'scale' || def.input_type === 'matrix' || def.input_type === 'ranking') {
    const role = def.input_type === 'scale' ? 'radiogroup' : 'group';
    return (
      <div
        className={groupClass} role={role} aria-labelledby={`${domId}-label`}
        aria-required={ariaReq} {...errorAttrs(domId, error, path, hasInfo)}
      >
        <label id={`${domId}-label`}>{def.label}{marker}</label>
        {def.input_type === 'scale' && <ScaleInput def={def} domId={domId} value={value} onChange={change} disabled={disabled} />}
        {def.input_type === 'matrix' && <MatrixInput def={def} domId={domId} value={value} onChange={change} disabled={disabled} />}
        {def.input_type === 'ranking' && <RankingInput def={def} domId={domId} value={value} onChange={change} disabled={disabled} />}
        {err}
        {noteEl}
        {info}
      </div>
    );
  }

  if (def.input_type === 'name') {
    // normalizeNameValue keeps every key present (null when blank) and derives
    // fullName on each edit, so submissions carry both the combined and the
    // separate parts.
    const cur = normalizeNameValue((value && typeof value === 'object' && !Array.isArray(value) ? value : {}) as Record<string, unknown>);
    const patch = (k: 'firstName' | 'lastName', v: string) => { onClearError?.(k); onChange(normalizeNameValue({ ...cur, [k]: v })); };
    const sub = (k: 'firstName' | 'lastName', label: string) => {
      const subErr = subErrors[k];
      const subId = `${domId}-${k}`;
      return (
        <div className={`of-subfield${subErr ? ' has-error' : ''}`}>
          <input
            id={subId} type="text" value={String(cur[k] ?? '')}
            disabled={disabled || lockedSubs.includes(k)}
            aria-label={`${def.label} — ${label}`} aria-required={ariaReq}
            autoComplete={NAME_AUTOCOMPLETE[k]} enterKeyHint="next"
            {...errorAttrs(subId, subErr, `${path}.${k}`, false)}
            onChange={(e) => patch(k, e.target.value)}
          />
          <label htmlFor={subId} className="of-sublabel">{label}</label>
          <FieldError id={`${subId}-err`} text={subErr} />
        </div>
      );
    };
    return (
      <div className={groupClass} role="group" aria-labelledby={`${domId}-label`}>
        <label id={`${domId}-label`}>{def.label}{marker}</label>
        <div className="of-row-2 of-composite-row">
          {sub('firstName', S.sub.firstName)}
          {sub('lastName', S.sub.lastName)}
        </div>
        {err}
        {noteEl}
        {info}
      </div>
    );
  }

  if (def.input_type === 'address') {
    // normalizeAddressValue keeps every key present (null when blank) and
    // derives fullAddress on each edit — combined and separate parts both ship.
    const shipping = m.address?.kind === 'shipping';
    const cur = normalizeAddressValue(
      (value && typeof value === 'object' && !Array.isArray(value) ? value : {}) as Record<string, unknown>,
      { shipping }
    );
    const defaultCountry = String(m.address?.defaultCountry || '');
    const country = String(cur.country ?? defaultCountry);
    const fm = addressFormat(country || undefined);
    // Materialize the effective country with the first edit, so a submission
    // filled under the default country carries that country explicitly.
    const patch = (k: keyof AddressValue, v: string) => {
      onClearError?.(k);
      const next: Record<string, unknown> = { ...cur, [k]: v };
      if (k !== 'country' && !next.country && defaultCountry) next.country = defaultCountry;
      onChange(normalizeAddressValue(next, { shipping }));
    };
    const req = required && !disabled;
    // `of-sub-<part>` is how the phone layout picks the State box out of the
    // City-State-Zip line to give it a row of its own; nothing styles it above
    // 600px.
    const sub = (k: keyof AddressValue, label: string, input: ReactNode) => (
      <div className={`of-subfield of-sub-${k}${subErrors[k] ? ' has-error' : ''}`} key={k}>
        {input}
        <label htmlFor={`${domId}-${k}`} className="of-sublabel">{label}</label>
        <FieldError id={`${domId}-${k}-err`} text={subErrors[k]} />
      </div>
    );
    const attrs = (k: keyof AddressValue) => errorAttrs(`${domId}-${k}`, subErrors[k], `${path}.${k}`, false);
    const text = (k: keyof AddressValue, label: string, isReq: boolean, inputType: 'text' | 'tel' = 'text') => sub(k, label, (
      <input
        id={`${domId}-${k}`} type={inputType} value={String(cur[k] ?? '')}
        disabled={disabled || lockedSubs.includes(k)}
        aria-label={`${def.label} — ${label}`} aria-required={isReq || undefined}
        autoComplete={ADDRESS_AUTOCOMPLETE[k]} enterKeyHint="next"
        {...attrs(k)}
        onChange={(e) => patch(k, e.target.value)}
      />
    ));
    const stateInput = fm.stateLabel && (fm.stateOptions
      ? sub('state', fm.stateLabel, (
        <select
          id={`${domId}-state`} value={String(cur.state ?? '')}
          disabled={disabled || lockedSubs.includes('state')}
          aria-label={`${def.label} — ${fm.stateLabel}`} aria-required={(req && fm.stateRequired) || undefined}
          autoComplete={ADDRESS_AUTOCOMPLETE.state}
          {...attrs('state')}
          onChange={(e) => patch('state', e.target.value)}
        >
          <option value="">{S.select}</option>
          {fm.stateOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      ))
      : text('state', fm.stateLabel, req && fm.stateRequired));
    const countryInput = sub('country', S.sub.country, (
      <select
        id={`${domId}-country`} value={country}
        disabled={disabled || lockedSubs.includes('country')}
        aria-label={`${def.label} — ${S.sub.country}`} aria-required={req || undefined}
        autoComplete={ADDRESS_AUTOCOMPLETE.country}
        {...attrs('country')}
        onChange={(e) => { onClearError?.('country'); onChange(normalizeAddressValue({ ...cur, country: e.target.value })); }}
      >
        <option value="">{S.select}</option>
        {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    ));
    // Postal-form convention: Address 1 / Address 2 / City–State–Zip on one
    // line / Country on its own line. Only the sub-fields this country
    // actually has appear — never an empty placeholder cell — with the city
    // taking the widest column.
    const middle: ReactNode[] = [
      text('city', fm.cityLabel, req),
      ...(stateInput ? [stateInput] : []),
      ...(fm.postalLabel ? [text('postalCode', fm.postalLabel, req)] : []),
    ];
    const middleCols = middle.length === 3 ? '2fr 1fr 1fr' : middle.length === 2 ? '2fr 1fr' : '1fr';
    return (
      <div className={groupClass} role="group" aria-labelledby={`${domId}-label`}>
        <label id={`${domId}-label`}>
          {def.label}
          {markerFor(req)}
        </label>
        <div className="of-composite">
          {shipping && text('company', S.sub.company, req)}
          {shipping && (
            <div className="of-composite-row of-addr-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              {text('attention', S.sub.attention, false)}
              {text('phone', S.sub.phone, false, 'tel')}
            </div>
          )}
          {text('addressLine1', S.sub.addressLine1, req)}
          {text('addressLine2', S.sub.addressLine2, false)}
          {/* `of-addr-locality` names the City-State-Zip line, which is the one
              the phone layout re-columns (City + ZIP, State on its own row);
              the Attention/Phone line above stacks as it always has. */}
          <div className="of-composite-row of-addr-row of-addr-locality" style={{ gridTemplateColumns: middleCols }}>{middle}</div>
          {countryInput}
        </div>
        {err}
        {noteEl}
        {info}
      </div>
    );
  }

  if (def.input_type === 'payment') {
    const pay = m.payment || {};
    const currency = String(pay.currency || 'usd').toUpperCase();
    const amount = pay.amountMode === 'field'
      ? Number(ctxValues[pay.amountField || ''] ?? 0) || 0
      : Number(pay.amountFixed ?? 0) || 0;
    return (
      <div className="of-group">
        <div className="of-payment">
          <div className="of-payment-head">
            <span className="of-payment-label">{def.label || S.payment}</span>
            <span className="of-payment-amount">
              {amount > 0 ? `${amount.toFixed(2)} ${currency}` : '—'}
            </span>
          </div>
          {pay.description && <div className="of-payment-desc">{pay.description}</div>}
          <div className="of-payment-note">
            {amount > 0 ? S.paymentAfterSubmit : S.paymentNothing}
          </div>
        </div>
        {noteEl}
        {info}
      </div>
    );
  }

  if (def.input_type === 'checkbox') {
    return (
      <div className={groupClass}>
        <div className="of-checkbox">
          <input
            id={domId} type="checkbox" checked={!!value} disabled={disabled}
            {...errorAttrs(domId, error, path, hasInfo)}
            onChange={(e) => change(e.target.checked)}
          />
          <label htmlFor={domId} style={{ marginBottom: 0 }}>{def.label}</label>
        </div>
        {err}
        {noteEl}
        {info}
      </div>
    );
  }

  if (def.input_type === 'signature') {
    return (
      <SignatureInput
        domId={domId} label={def.label} required={required} disabled={disabled}
        value={value} onChange={change}
        onMode={(mode) => onSignatureMode?.(mode)}
        signedAs={signatureMode}
        meta={m.signature} infoText={m.infoText} note={note}
        error={error} errorPath={path}
        // The same tokens the server fills when it records the terms, so what
        // is shown is what gets recorded (submittedAt is today's date there).
        ctxValues={{ ...ctxValues, ...(formName ? { formName } : {}), submittedAt: new Date().toISOString().slice(0, 10) }}
      />
    );
  }

  if (def.input_type === 'file') {
    return (
      <div className={groupClass}>
        <label htmlFor={domId}>{def.label}{marker}</label>
        {info}
        <FileInput
          domId={domId}
          files={files || []}
          onFiles={(list) => onFiles?.(list)}
          limits={resolveUploadLimits(m.upload, uploads)}
          disabled={disabled}
          required={required}
          error={error}
          errorPath={path}
          hasInfo={hasInfo}
          onClearError={onClearError ? () => onClearError() : undefined}
          onError={onSetError}
        />
        {noteEl}
      </div>
    );
  }

  const label = <label htmlFor={domId}>{def.label}{marker}</label>;
  const base = errorAttrs(domId, error, path, hasInfo);
  // The counter joins the description chain (help text, count, error) so a
  // screen reader hears the limit with the control.
  const a11y = counter
    ? { ...base, 'aria-describedby': [hasInfo ? `${domId}-info` : '', `${domId}-count`, error ? `${domId}-err` : ''].filter(Boolean).join(' ') }
    : base;
  const meta = counter ? <div className="of-meta">{info}{counter}</div> : info;

  // A select's "Other": the <option> carries a marker value the state never
  // sees — picking it opens the write-in, and the typed text is the answer.
  const current = String(value ?? '');
  const selOther = !!other && (isOther(current) || (otherOn && current === ''));

  return (
    <div className={groupClass}>
      {label}
      {def.input_type === 'select' ? (
        <>
          <select
            id={domId} value={selOther ? OTHER_VALUE : current} disabled={disabled} aria-required={ariaReq} {...a11y}
            onChange={(e) => {
              if (e.target.value === OTHER_VALUE) { setOtherOn(true); if (!isOther(current)) change(''); return; }
              setOtherOn(false);
              change(e.target.value);
            }}
          >
            <option value="">{S.select}</option>
            {choiceOpts.map((o) => <option key={o.value} value={o.value}>{o.text}</option>)}
            {other && <option value={OTHER_VALUE}>{other.label}</option>}
          </select>
          {selOther && otherInput(isOther(current) ? current : '', (t) => change(t))}
        </>
      ) : def.input_type === 'textarea' ? (
        <textarea
          id={domId} value={String(value ?? '')} placeholder={m.placeholder}
          disabled={disabled} aria-required={ariaReq}
          {...a11y}
          onChange={(e) => change(e.target.value)}
        />
      ) : (
        <input
          id={domId} type={def.input_type === 'datetime' ? 'datetime-local' : def.input_type}
          // A US phone (U3) is held as digits and shown grouped; every
          // keystroke is reduced to digits again, so the payload, the draft
          // and the validator only ever see the ten. A date-time (P3) is
          // stored with the respondent's offset and shown without it.
          value={usPhone ? formatUsPhone(current) : def.input_type === 'datetime' ? datetimeLocalPart(current) : current}
          placeholder={m.placeholder || (usPhone ? S.phonePlaceholder : undefined)}
          disabled={disabled} aria-required={ariaReq}
          // What the browser may offer to fill in, and what Enter should do
          // next (M2/C3). Neither changes the answer or the payload.
          autoComplete={autoCompleteFor(def)} enterKeyHint={enterKeyHintFor(def.input_type)}
          // A date window is enforced by the picker itself, so an out-of-range
          // day cannot be chosen in the first place — the message on submit is
          // the backstop for a typed one. Length and pattern rules are NOT
          // attributes any more: the engine's validator reports them under the
          // control, and a maxLength that clipped typing would hide the
          // problem instead of naming it.
          min={def.input_type === 'number' ? rules.min
            : def.input_type === 'date' ? resolveDateBound(rules.dateMin) ?? undefined
            : def.input_type === 'datetime' ? dayBound(rules.dateMin, 'T00:00') : undefined}
          max={def.input_type === 'number' ? rules.max
            : def.input_type === 'date' ? resolveDateBound(rules.dateMax) ?? undefined
            : def.input_type === 'datetime' ? dayBound(rules.dateMax, 'T23:59') : undefined}
          {...a11y}
          onChange={(e) => change(usPhone ? usPhoneDigits(e.target.value)
            : def.input_type === 'datetime' ? withLocalOffset(e.target.value) : e.target.value)}
        />
      )}
      {err}
      {noteEl}
      {meta}
    </div>
  );
}
